import ApprovalActionButton from './components/ApprovalActionButton'
import ApprovalOptionTag from './components/ApprovalOptionTag'
import type { ApprovalFieldCellProps } from './types'

/**
 * Displays each active selected approval option with its current state and
 * request-user-specific controls.
 *
 * The field value determines visible option order and state, while
 * `approvalActions` only supplies legal controls when an action handler exists.
 * Missing actions leave saved decisions visible, including on Closed records.
 * Action metadata does not change the stored selection order.
 */
function ApprovalFieldCell({
  approvalActions,
  fieldValue,
  onAction,
  pending = false,
  pendingAction = null,
  recordId,
}: ApprovalFieldCellProps) {
  const actionsByOptionId = new Map(
    approvalActions?.options.map((option) => [option.optionId, option.actions])
  )
  const options = fieldValue.value.filter(({ optionDeleted }) => !optionDeleted)
  const hasActions =
    !!onAction &&
    options.some(
      ({ optionId }) => (actionsByOptionId.get(optionId)?.length ?? 0) > 0
    )

  return (
    <div
      style={{
        display: 'grid',
        gap: '7px 24px',
        gridTemplateColumns: hasActions
          ? 'max-content minmax(0, 1fr)'
          : 'minmax(0, 1fr)',
        padding: 2,
      }}
    >
      {options.map((option) => {
        const actions = actionsByOptionId.get(option.optionId) ?? []

        return (
          <div
            key={option.optionId}
            style={{
              display: 'grid',
              alignItems: 'center',
              gridColumn: '1 / -1',
              gridTemplateColumns: 'subgrid',
              minWidth: 0,
            }}
          >
            {onAction && actions.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gridColumn: 1,
                  gap: 4,
                  minWidth: 68,
                }}
              >
                {actions.map((action) => (
                  <ApprovalActionButton
                    key={action.action}
                    action={action}
                    onAction={onAction}
                    optionId={option.optionId}
                    optionLabel={option.optionLabel}
                    pending={pending}
                    pendingAction={pendingAction}
                    recordId={recordId}
                  />
                ))}
              </div>
            )}
            <ApprovalOptionTag option={option} />
          </div>
        )
      })}
    </div>
  )
}

export default ApprovalFieldCell
